<template>
		<anchor-element v-if="visible" tag="div" :chain="chain" class="fd-user-select fd-input-like" :class="[clazz, errorClass, extraClasses]" :style="style"
						v-bind="tabindexBinding(0, true)"
						@focus="componentFocused" @blur="componentBlurred"
						@keydown="onKeyDown" @mousedown="fieldMouseDown" ref="anchor">
			<template v-for="(item) in wrapSegmentPairs" :key="item.key">
				<span v-if="item.q && item.p" class="us-pair">
					<tag-input v-if="segmentIsQuery(item.q)" :min-width="item.q.last ? 32 : 6" class="upq" v-model="item.q.query"
							   @focus="queryFocused(item.q.index)"
							   @blur="queryBlurred(item.q.index)"
							   @paste="pasteValue"
							   @keydown="onQueryKeyDown($event, item.q.index)"></tag-input>
					<user-party v-if="segmentIsParty(item.p)" :id="item.p.storeAs" :kind="item.p.kind" :name="item.p.name" :title="item.p.title" :selected="!!item.p.selected" :disabled="ro" :avatar="item.p.avatar" :icon="item.p.icon" @click="selectClick($event,item.p)"></user-party>
				</span>
				<tag-input v-else-if="segmentIsQuery(item.q)" :min-width="item.q.last ? 32 : 6" class="upq" v-model="item.q.query"
						   @focus="queryFocused(item.q.index)"
						   @blur="queryBlurred(item.q.index)"
						   @paste="pasteValue"
						   @keydown="onQueryKeyDown($event, item.q.index)"></tag-input>
				<user-party v-else-if="segmentIsParty(item.p)" :id="item.p.storeAs" :kind="item.p.kind" :name="item.p.name" :title="item.p.title" :selected="!!item.p.selected" :disabled="ro" :avatar="item.p.avatar" :icon="item.p.icon" @click="selectClick($event,item.p)"></user-party>
			</template>
		</anchor-element>
		<boat-element tag="div" :chain="chain" class="fd-drop fd-user-select-drop" ref="boat">
			<template v-if="suggestionCount > 0">
				<template v-for="si in suggMenuItems" :key="si.id">
					<div v-if="isSeparator(si)" class="us-separ">{{ si.title }}</div>
					<user-party-suggestion v-else :item="si" :selected="si.id === selectedSuggestion"
										   @mouseenter="suggestionMouseEnter($event, si)"
										   @mouseleave="suggestionMouseLeave($event, si)"
										   @expand="expandQuery"
										   @click="addSuggestion(si)"></user-party-suggestion>
				</template>
			</template>
			<template v-else>
				<div class="us-not-found">{{ env.msg('suggest.not.found', query) }}</div>
			</template>
		</boat-element>
</template>

<script setup lang="ts">
import {ref, computed, nextTick, watchEffect, watch} from "vue";
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor.ts";
import {SuggestedUserParty, UserSelect, UserSelectResponse} from "@t/fd/editor/UserSelect.ts";
import {ChainRule, createChain} from "@/ui/paneltree/PanelTree.ts";
import {PartySegment, QuerySegment, SegmentWrapPair, USegment, usePartyTags, UserPartyKind} from "@fd/editors/user-select/UserSelectUtils.ts";
import UserParty from "@fd/editors/user-select/UserParty.vue";
import TagInput from "@fd/editors/suggester/TagInput.vue";
import {injectEnv} from "@fd/Environment.ts";
import UserPartySuggestion from "@fd/editors/user-select/UserPartySuggestion.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import {calmTrigger} from "@/utils/Debounce.ts";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

/** The part */
const us = computed<UserSelect>(() => props.part as UserSelect);
const {visible, value, clazz, style, ro, errorClass, tabindexBinding} = useEditor(props);

// If a query is focused, this will be the index, -1 otherwise
const focusedQueryIndex = ref<number>(-1);
// contains last focus index event if the focus is already lost
const lastFocusedQueryIndex = ref<number>(-1);
// true if the whole component is focused (not a query)
const fieldFocused = ref<boolean>(false);
const focused = computed<boolean>(() => fieldFocused.value || focusedQueryIndex.value >= 0);
/** This variable blocks calls to the backend until the first focus happens */
const sleep = ref<boolean>(true);

const anchor = ref<InstanceType<typeof AnchorElement> | null>(null);
const boat = ref<InstanceType<typeof BoatElement> | null>(null);
const rootElement = computed<HTMLElement|null>(() => anchor.value ? anchor.value.element() : null);

const extraClasses = computed(() => {
	const c: string[] = [];
	if (ro.value) c.push("fd-user-select-disabled");
	if (!allowQueries.value) c.push("fd-user-select-no-queries");
	return c;
});

const array = computed<(string|number)[]>(() => {
	return Array.isArray(value.value) ? value.value : [];
});

const sys = computed<SuggestedUserParty[]|null|undefined>(() => {
	return us.value?.system;
});

const { partyTags, partyMap, normalized, uniqueCount, contains } = usePartyTags(env, array, sys);

const canAdd = computed(() => {
	return typeof us.value.max === "number" ? array.value.length < us.value.max : true;
});
const canDelete = computed(() => {
	return typeof us.value.min === "number" ? array.value.length > us.value.min : array.value.length > 0;
});
const allowQueries = computed(() => { return !ro.value && canAdd.value; });

// Queries go here
// All query editors are stored here. There should be array.length + 1 items
const editors = ref<QuerySegment[]>([]);

// Current list of loaded suggestions
const suggestions = ref<UserSelectResponse>({
	users: [],
	groups: [],
	parties: []
});
// Number of suggestions currently loaded
const suggestionCount = computed<number>(() => {
	const s = suggestions.value;
	return (s?.system?.length ?? 0) + (s?.users?.length ?? 0) + (s?.groups?.length ?? 0) + (s?.parties?.length ?? 0);
});
// This suggestion will be highlighted in the drop-down
const selectedSuggestion = ref<number>(0);	// ID of a selected suggestion

// Convert suggestions to a list of segments for the drop-down
const suggMenuItems = computed<(SuggestedUserParty|SuggestionSeparator)[]>(() => {
	const sitems = [];
	if (Array.isArray(suggestions.value.system) && suggestions.value.system.length > 0) {
		sitems.push(SYSTEM_SEPARATOR);
		sitems.push(...suggestions.value.system);
	}
	if (Array.isArray(suggestions.value.users) && suggestions.value.users.length > 0) {
		sitems.push(USERS_SEPARATOR);
		sitems.push(...suggestions.value.users);
	}
	if (Array.isArray(suggestions.value.groups) && suggestions.value.groups.length > 0) {
		sitems.push(GROUPS_SEPARATOR);
		sitems.push(...suggestions.value.groups);
	}
	if (Array.isArray(suggestions.value.parties) && suggestions.value.parties.length > 0) {
		sitems.push(PARTIES_SEPARATOR);
		sitems.push(...suggestions.value.parties);
	}
	return sitems;
});

const typedQuery = ref<string>("");
const expansionQuery = ref<string|null>(null);
const query = computed<string>(() => {
	if (expansionQuery.value) return expansionQuery.value;
	return typedQuery.value;
});

const above:ChainRule = {
	ah: 0, av: 0,
	bh: 0, bv: 100,
	x: -2, y: -3 };
const bellow:ChainRule = {
	ah: 0, av: 100,
	bh: 0, bv: 0,
	x: -2, y: 3 };

const chain = createChain({
	managed: true,
	rule: (m:ElementMeasure) => {
		if (m.spaceBottom >= 300 || m.spaceBottom > m.spaceTop) {
			return { ...bellow, spaceLimit: m.spaceBottom };
		} else {
			return { ...above, spaceLimit: m.spaceTop };
		}
	},
}, "user-select");

const dropVisible = computed<boolean>(() => {
	return (chain.interacting.value || focused.value)
		&& (canAdd.value || anyTagSelected.value);	// it is possible to add more tags or there is a selected tag which will be replacet by a new tag
});

const anyTagSelected = computed<boolean>(() => {
	for (let t of partyTags.value) {
		if (t.selected) return true;
	}
	return false;
});

/***** Suggestion separators *****/
interface SuggestionSeparator {
	id: number;
	title: string;
}
const USERS_SEPARATOR:SuggestionSeparator = {id: -100100, title: env.msg('menu.users')};
const GROUPS_SEPARATOR:SuggestionSeparator = {id: -100200, title: env.msg('menu.viewGroups')};
const PARTIES_SEPARATOR:SuggestionSeparator = {id: -100300, title: env.msg('menu.viewCustomers')};
const SYSTEM_SEPARATOR:SuggestionSeparator = {id: -100400, title: env.msg('menu.system')};

function isSeparator(s: SuggestedUserParty|SuggestionSeparator): s is SuggestionSeparator {
	return s.id < -100000;
}



function fieldMouseDown(e:MouseEvent) {
	/*e.preventDefault();
	e.stopPropagation();*/
}

function expandQuery(q:string) {
	expansionQuery.value = q;
}

const endpoint = computed<string>(() => {
	if (ro.value || sleep.value) return "";
	let ep = "api/parties/suggest";
	ep = env.net.urlParam(ep, "q", query.value);
	ep = env.net.urlParam(ep, "users", !!us.value.users);
	ep = env.net.urlParam(ep, "groups", !!us.value.groups);
	ep = env.net.urlParam(ep, "parties", !!us.value.parties);
	ep = env.net.urlParam(ep, "limit", 50);
	ep = env.net.urlParam(ep, "inactive", !!us.value.inactive);
	ep = env.net.urlParam(ep, "disallowed", !!us.value.disallowed);
	ep = env.net.urlParam(ep, "sysgroups", !!us.value.sysgroups);
	if (us.value.parties && Array.isArray(us.value.partyTypes) && us.value.partyTypes.length > 0) {
		ep = env.net.urlParam(ep, "partyTypes", us.value.partyTypes.join(','));
	}
	const ex:number[] = normalized.value;
	if (ex.length > 0) ep = env.net.urlParam(ep, "exclude", ex.join(','));

	return ep;
});

function nameMatchQuery(name:string, query:string):boolean {
	const n = name.toLowerCase();
	const q = query.toLowerCase();
	return n.indexOf(q) >= 0;
}

async function loadSuggestions() {
	let ep = endpoint.value;
	if (ep.length === 0) return;
	const resp = await env.net.fetch(ep);
	if (resp.ok) {
		suggestions.value = await resp.json() as UserSelectResponse;
		suggestions.value.system = [];
		if (us.value.system && Array.isArray(us.value.system) && us.value.system.length > 0) {
			for (let s of us.value.system) {
				if (query.value.trim().length === 0 || nameMatchQuery(s.name, query.value)) {
					if (!contains(s.id, s.login)) suggestions.value.system.push(s);
				}
			}
		}
	} else {
		// TODO What can we do on an error
		suggestions.value = { system: [], users: [], groups: [], parties: [] };
	}
	selectFirstSuggestion();
}

const debouncedLoadSuggestions = calmTrigger(loadSuggestions, 300);

watchEffect(() => {
	if (endpoint.value.length > 0) debouncedLoadSuggestions();
});

const suggestionIds = computed<number[]>(() => {
	const ids:number[] = [];
	if (Array.isArray(suggestions.value?.system)) {
		for (let s of suggestions.value.system) ids.push(s.id);
	}
	if (Array.isArray(suggestions.value?.users)) {
		for (let s of suggestions.value.users) ids.push(s.id);
	}
	if (Array.isArray(suggestions.value?.groups)) {
		for (let s of suggestions.value.groups) ids.push(s.id);
	}
	if (Array.isArray(suggestions.value?.parties)) {
		for (let s of suggestions.value.parties) ids.push(s.id);
	}
	return ids;
});

function suggestionMouseEnter(e:MouseEvent, s:SuggestedUserParty):void {
	selectedSuggestion.value = s.id;
}

function suggestionMouseLeave(e:MouseEvent, s:SuggestedUserParty):void {
	selectedSuggestion.value = 0;
}

function selectFirstSuggestion() {
	const ids = suggestionIds.value;
	if (ids.length > 0) selectedSuggestion.value = ids[0];
	else selectedSuggestion.value = 0;
}

function selectLastSuggestion() {
	const ids = suggestionIds.value;
	if (ids.length > 0) selectedSuggestion.value = ids[0];
	else selectedSuggestion.value = 0;
}

function selectNextSuggestion() {
	try {
		const ids = suggestionIds.value;
		for (let i = 0; i < ids.length; i++) {
			if (selectedSuggestion.value === ids[i]) {
				if (i + 1 < ids.length) selectedSuggestion.value = ids[i + 1];
				else selectedSuggestion.value = ids[0];
				return;
			}
		}
		selectFirstSuggestion();
	} finally {
		ensureSelectionVisible();
	}
}

function selectPrevSuggestion() {
	try {
		const ids = suggestionIds.value;
		for (let i = 0; i < ids.length; i++) {
			if (selectedSuggestion.value === ids[i]) {
				if (i > 0) selectedSuggestion.value = ids[i - 1];
				else selectedSuggestion.value = ids[ids.length - 1];
				return;
			}
		}
		selectLastSuggestion();
	} finally {
		ensureSelectionVisible();
	}
}

function ensureSelectionVisible() {
	nextTick(() => {
		if (boat.value) {
			const bel = boat.value.element();
			if (bel) {
				bel.querySelector(".us-item-selected")?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
			}
		}
	});
}

function addSuggestion(sup:SuggestedUserParty):void {
	replaceSelected(sup, true);
}

function addSuggestionByEnter():void {
	if (selectedSuggestion.value > 0) {
		const s = findSuggestion(selectedSuggestion.value);
		if (s?.s) {
			replaceSelected(s.s, true);
		}
	}
}



const wrapSegmentPairs = computed<SegmentWrapPair[]>(() => {
	const pairs:SegmentWrapPair[] = [];
	let edIdx = 0;
	const aq = allowQueries.value;
	for (let tag of partyTags.value) {
		const pair: SegmentWrapPair = { key: tag.key };
		if (aq) pair.q = notLast(queryAt(edIdx++));
		pair.p = tag;
		pairs.push(pair);
	}
	if (aq) {
		const qed = last(queryAt(edIdx));
		pairs.push({ q: qed, key: qed.key });
	}
	return pairs;

});

// const editorSegments = computed<USegment[]>(() => {
// 	const segs: USegment[] = [];
// 	let edIdx = 0;
// 	const aq = allowQueries.value;
// 	for (let tag of partyTags.value) {
// 		if (aq) segs.push(notLast(queryAt(edIdx++)));
// 		segs.push(tag);
// 	}
// 	if (aq) segs.push(last(queryAt(edIdx)));
// 	return segs;
// });
//

const segmentId = ref(0);
/* Key generating - for efficient list rendering */
function nextId(): string {
	return "seg" + ++segmentId.value;
}

function queryAt(idx: number): QuerySegment {
	while (idx >= editors.value.length) editors.value.push(newQuery(idx));
	return editors.value[idx];
}

function emptyAllQueries():void {
	for (let q of editors.value) {
		q.query = "";
	}
}

function last(qs:QuerySegment):QuerySegment {
	qs.last = true;
	return qs;
}

function notLast(qs:QuerySegment):QuerySegment {
	qs.last = false;
	return qs;
}

function newQuery(idx: number): QuerySegment {
	return {
		key: nextId(),
		type: "query",
		query: "",
		index: idx
	};
}


function segmentIsQuery(segment: USegment): segment is QuerySegment {
	return segment?.type === 'query';
}

function segmentIsParty(segment: USegment): segment is PartySegment {
	return segment?.type === 'party';
}

/** Returns true if the array already contains the value */
function containsValue(val: string | number): boolean {
	return array.value.findIndex((v) => v === val) >= 0;
}

function unselectAll() {
	/* for (let tag of partyTags.value) {
		tag.selected = false;
	}*/
	for (let tag of Object.values(partyMap.value)) {
		tag.selected = false;
	}
}

function resetAllQueries() {
	for (let q of editors.value) {
		q.query = "";
	}
}

function selectAll() {
	for (let tag of partyTags.value) {
		tag.selected = true;
	}
}

function copySelected(cut = false):boolean {
	if (cut && (ro.value || !canDelete.value)) return false;
	const sidxs = selectedIndexes();
	const result:any[] = [];
	for (let idx of sidxs) {
		result.push(array.value[idx]);
	}
	const str = JSON.stringify(result);
	navigator.clipboard.writeText(str);
	if (cut) {
		return deleteSelected(true);
	}
}

/** Return the indexes of selected items in normal or reverse order */
function selectedIndexes(descOrder = false): number[] {
	const idxs:number[] = [];
	let idx = 0;
	for (let tag of partyTags.value) {
		if (tag.selected) {
			if (descOrder) idxs.splice(0, 0, idx);
			else idxs.push(idx);
		}
		idx++;
	}
	return idxs;
}


function selectedValues():(number|string)[] {
	const ids:(number|string)[] = [];
	for (let tag of partyTags.value) {
		if (tag.selected) ids.push(tag.storeAs);
	}
	return ids;
}

function selectedNormalizedValues():number[] {
	const ids:number[] = [];
	for (let tag of partyTags.value) {
		if (tag.selected && typeof tag.id === "number") ids.push(tag.id);
	}
	// Deduplicate
	return [...new Set(ids)];
}

function unselectedNormalizedValues():number[] {
	const ids:number[] = [];
	for (let tag of partyTags.value) {
		if (!tag.selected && typeof tag.id === "number") ids.push(tag.id);
	}
	// Deduplicate
	return [...new Set(ids)];
}


// function selectedValues():(number|string)[] {
// 	for (let tag of partyTags.value) {
// 		if (tag.selected) {
//
// 		}
// 	}
// }

/**
 * Delete selected items and returns true if the operation has changed the underlying value
 */
function deleteSelected(doFocus = true):boolean {
	if (ro.value || !canDelete.value) return false;
	const sidxs = selectedIndexes(true);
	if (sidxs.length === 0 || (typeof us.value.min === "number" && (array.value.length - sidxs.length) < us.value.min)) return false;	// cannot remove under the min
	for (let i = 0; i < sidxs.length; i++) {
		value.value.splice(sidxs[i], 1);
	}
	unselectAll();
	if (doFocus) {
		const focusIndex = sidxs[0] - sidxs.length + 1;
		focusQuery(focusIndex);
	}
	return true;
}

function findKindOfSuggestion(sup:SuggestedUserParty):UserPartyKind {
	return findSuggestion(sup.id).kind;
}

function findSuggestion(id:number):{ s: SuggestedUserParty|null, kind:UserPartyKind } {
	if (Array.isArray(suggestions.value?.system) && suggestions.value.system.length > 0) {
		for (let s of suggestions.value.system) {
			if (s.id === id) return { s, kind: "system" };
		}
	}
	if (Array.isArray(suggestions.value.users) && suggestions.value.users.length > 0) {
		for (let s of suggestions.value.users) {
			if (s.id === id) return { s, kind: "user" };
		}
	}
	if (Array.isArray(suggestions.value.groups) && suggestions.value.groups.length > 0) {
		for (let s of suggestions.value.groups) {
			if (s.id === id) return { s, kind: "group" };
		}
	}
	if (Array.isArray(suggestions.value.parties) && suggestions.value.parties.length > 0) {
		for (let s of suggestions.value.parties) {
			if (s.id === id) return { s, kind: "party" };
		}
	}
	return { s:null, kind: "missing" };
}

function insertSuggestion(idx:number, sup:SuggestedUserParty):void {
	if (idx < 0) idx = 0;
	else if (idx > array.value.length) idx = array.value.length;
	const storeAs:(number|string) = us.value.preferLogins && sup.login ? sup.login : sup.id;
	if (!partyMap.value[storeAs]) {
		const s:PartySegment = {
			key: storeAs.toString(),
			id: sup.id,
			login: sup.login,
			index:idx,
			type:"party",
			storeAs: storeAs,
			kind: findKindOfSuggestion(sup),
			name: sup.name,
			avatar: sup.avatar,
			icon: sup.icon,
			selected: false
		};
		partyMap.value[storeAs] = s;
	}
	value.value.splice(idx, 0, storeAs);
	// Correct indexes
	let ix = 0;
	for (let tag of partyTags.value) {
		tag.index = ix++;
	}
	emptyAllQueries();
}

function replaceSelected(sup:SuggestedUserParty, doFocus = true):boolean {
	if (ro.value) return false;
	//debugger;
	if (contains(sup.id, sup.login)) return false;	// item already there
	let count = uniqueCount.value;		// current unique count of items


	const selectedTags = selectedNormalizedValues().length;
	const isFromQuery = selectedTags === 0;

	// decrease the count for the each selected item, those will be removed
	count -= selectedTags - 1;
	// The count is now target count after intended operation. It must be in the min..max range
	if (typeof us.value?.min === "number" && count < us.value.min) return false;
	if (typeof us.value?.max === "number" && count > us.value.max) return false;

	let insertIndex: number;
	if (isFromQuery) {
		insertIndex = focusedQueryIndex.value;
		if (insertIndex < 0) insertIndex = lastFocusedQueryIndex.value;
		if (insertIndex < 0) insertIndex = array.value.length;
	} else {
		// Remove selection
		const sidxs = selectedIndexes(true);
		for (let i = 0; i < sidxs.length; i++) {
			value.value.splice(sidxs[i], 1);
		}
		insertIndex = sidxs[0] - sidxs.length + 1;
	}
	insertSuggestion(insertIndex, sup);

	if (doFocus) {
		console.log("....doing focus allowQueries="+allowQueries.value);
		if (allowQueries.value) focusQuery(insertIndex + 1);
		else {
			if (!isFromQuery) selectTagByValue(sup.id);
			focusComponent();
		}
	}
	return true;
}

function selectClick(e: MouseEvent, segment:PartySegment):void {
	expansionQuery.value = null;
	e.preventDefault();
	if (!e.shiftKey) unselectAll();
	segment.selected = !segment.selected;
	sleep.value = false;
}

function selectTag(idx: number, unselectOthers = false):void {
	if (unselectOthers) unselectAll();
	const tag = partyTags.value[idx];
	tag.selected = true;
}

function findTag(storeValue: number|string): PartySegment|null {
	for (let tag of Object.values(partyMap.value)) {
		if (typeof storeValue === "number") {
			if (tag.id === storeValue) return tag;
		} else {
			if (tag.login === storeValue) return tag;
		}
	}
	return null;
}

function selectTagByValue(value: number|string, unselectOthers = false):void {
	if (unselectOthers) unselectAll();
	const tag = findTag(value);
	if (tag) tag.selected = true;
}

let stopQueryWatcher = null;

function queryFocused(idx: number) {
	if (stopQueryWatcher) {
		stopQueryWatcher();
		stopQueryWatcher = null;
	}

	const q = queryAt(idx);
	stopQueryWatcher = watch(() => q.query, (val) => {
		typedQuery.value = val.trim();
	}, { immediate: true });

	unselectAll();
	expansionQuery.value = null;
	focusedQueryIndex.value = idx;
	sleep.value = false;
}

function queryBlurred(idx: number) {
	if (stopQueryWatcher) {
		stopQueryWatcher();
		stopQueryWatcher = null;
	}

	if (focusedQueryIndex.value >= 0) lastFocusedQueryIndex.value = focusedQueryIndex.value;
	focusedQueryIndex.value = -1;
}

function componentFocused() {
	fieldFocused.value = true;
}
function componentBlurred() {
	fieldFocused.value = false;
}

/** Move cursor to the query with given index */
function focusQuery(idx: number) {
	if (!allowQueries.value) focusComponent();
	else {
		nextTick(() => {
			if (rootElement.value) {
				const qelms = rootElement.value.querySelectorAll(".upq");
				if (idx < qelms.length) (qelms[idx] as HTMLElement).focus();
			}
		});
	}
}

function focusComponent() {
	console.log("plan to focus component");
	nextTick(() => {
		console.log("next tick focus component");
		if (rootElement.value) {
			console.log("next tick calling focus");
			rootElement.value.focus();
		}
	});
}

function focusSibling(prev = false):boolean {
	if (focusedQueryIndex.value < 0) {
		const sidxs = selectedIndexes();
		if (sidxs.length === 0) return false;
		if (allowQueries.value) {
			let targetQueryIndex = prev ? Math.min(...sidxs) : Math.max(...sidxs) + 1;
			focusQuery(targetQueryIndex);
		} else {
			let targetTagIndex = prev ? Math.min(...sidxs) - 1 : Math.max(...sidxs) + 1;
			if (targetTagIndex < 0 || targetTagIndex >= array.value.length) return false;
			rootElement.value?.focus();
			selectTag(targetTagIndex, true);
		}
	} else {
		const fq = focusedQueryIndex.value;
		if (prev && fq === 0) return false;	// can't prev
		if (!prev && fq === array.value.length) return false; // can't next
		rootElement.value?.focus();
		selectTag(prev ? fq - 1 : fq, true);
	}
	return true;
}

function onKeyDown(e: KeyboardEvent) {
	//console.log("onKeyDown", e.key, "shift="+e.shiftKey, "ctrl="+e.ctrlKey, "meta="+e.metaKey);
	if (e.key === "Backspace" || e.key === "Delete") {
		if (deleteSelected()) e.preventDefault();
	} else if (e.key === "a" && (e.ctrlKey || e.metaKey)) {
		if (focusedQueryIndex.value < 0) {
			selectAll();
			e.preventDefault();
		}
	} else if ((e.key === "c" || e.key === "x") && (e.ctrlKey || e.metaKey)) {
		if (focusedQueryIndex.value < 0) {
			if (copySelected(e.key === "x")) e.preventDefault();
		}
	} else if (e.key === "Tab") {
		if (focusSibling(e.shiftKey)) e.preventDefault();
	} else if (e.key === "ArrowUp") {
		if (suggestionCount.value > 0) {
			selectPrevSuggestion();
			e.preventDefault();
		}
	} else if (e.key === "ArrowDown") {
		if (suggestionCount.value > 0) {
			selectNextSuggestion();
			e.preventDefault();
		}
	} else if (e.key === "Enter") {
		if (suggestionCount.value > 0) {
			addSuggestionByEnter();
			e.preventDefault();
		}
	}
}

function onQueryKeyDown(e: KeyboardEvent, idx: number) {
}

function pasteValue(e: ClipboardEvent) {
	e.preventDefault();
	const text = e.clipboardData.getData("text/plain");
	try {
		const parsed = JSON.parse(text);
		if (Array.isArray(parsed)) {
			const rightType = parsed.filter((v) => typeof v === "string" || typeof v === "number");
			const uniqueValues = rightType.filter((v) => !containsValue(v));
			if (uniqueValues.length > 0) {
				// Really insert at index or at the end
				if (focusedQueryIndex.value < 0) value.value.push(...uniqueValues);
				else {
					value.value.splice(focusedQueryIndex.value, 0, ...uniqueValues);
					focusQuery(focusedQueryIndex.value + uniqueValues.length);
				}
				e.preventDefault();
			}
		}
	} catch (e) {
		// Probably not a valid JSON
		return;
	}
}

let visibilityTimeout = 0;
function chainVisible() { chain.visible.value = true; visibilityTimeout = 0; }
function chainHidden() { chain.visible.value = false; visibilityTimeout = 0; }

function setDropVisibility(visible: boolean) {
	if (visibilityTimeout) clearTimeout(visibilityTimeout);
	visibilityTimeout = setTimeout(visible ? chainVisible : chainHidden, 100);
}

watchEffect(() => { setDropVisibility(dropVisible.value); });

/** Unselect all tags on a long blur **/

const MAX_BLUR_MILLIS = 1000;

const keepSelection = computed<boolean>(() => {
	return chain.interacting.value || focused.value;
});

let unselectionTimeout = 0;

watch(keepSelection, (s:boolean) => {
	if (s) {
		if (unselectionTimeout) {
			clearTimeout(unselectionTimeout);
			unselectionTimeout = 0;
		}
	} else {
		// Blurred
		unselectionTimeout = setTimeout(() => {
			unselectAll();
			resetAllQueries();
		}, MAX_BLUR_MILLIS);
	}
});


</script>

<style>
.fd-user-select {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	flex-wrap: wrap;
	column-gap: 0; /* should be filled with the editor */
	row-gap: 1px;

	/* padding: 0 var(--input-h-padding); */
	padding:0;
	min-height: var(--input-height);
	background-color: var(--bg);
	color: var(--fg);
	margin: 0;
	font-size: var(--input-font-size);
	line-height: var(--input-height);
	border-radius: var(--input-radius);
	box-shadow: var(--input-offset-shadow);
	font-family: var(--font);
	cursor: default;

	&:focus-visible,
	&:focus-within,
	&:focus {
		outline: none;
		box-shadow: 0 0 0 1px var(--bg), 0 0 2px 3px var(--outline-color);
	}

	&.fd-user-select-disabled {
		background-color: var(--input-disabled-bg);
		color: var(--input-disabled-fg);
		box-shadow: var(--input-disabled-offset-shadow);
	}
	&.fd-user-select-no-queries {
		column-gap: 6px;
		padding-inline: var(--input-h-padding);
	}
}
.us-pair {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	align-items: baseline;
}

.upq {
	/* Query less visible than normal text */
	color: color-mix(in srgb, var(--fg) 66%, transparent);
}

.fd-user-select-drop {
	border-radius: calc(var(--input-radius) + 2px);
	padding-bottom: 8px;
	width: max(calc(var(--anchor-width) + 4px), 250px);
	max-height: calc(var(--space-limit) - 6px);
	overflow-y: auto;

	& > .us-separ {
		padding: 8px 8px 4px 8px;
		line-height: 16px;
		font-size: var(--font-size-s);
		color: var(--menu-separator-fg);
	}

	& > .us-not-found {
		padding: 8px 8px 0 8px;
		opacity: 0.5;
	}
}
</style>